/**
 * Period Filter Component — NF Tracker
 * Unificado para Minhas Notas, Dashboard e Análise
 */

export function filterNotasByPeriod(notas, period = 'month', customStart = null, customEnd = null) {
  if (!Array.isArray(notas)) return [];
  const now = new Date();

  if (period === 'year') {
    const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
    return notas.filter(n => {
      if (!n.dataEmissao) return false;
      return new Date(n.dataEmissao) >= oneYearAgo;
    });
  }

  if (period === '3months') {
    const threeMonthsAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    return notas.filter(n => {
      if (!n.dataEmissao) return false;
      return new Date(n.dataEmissao) >= threeMonthsAgo;
    });
  }

  if (period === 'month') {
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    return notas.filter(n => {
      if (!n.dataEmissao) return false;
      const d = new Date(n.dataEmissao);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    });
  }

  if (period === 'week') {
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return notas.filter(n => {
      if (!n.dataEmissao) return false;
      return new Date(n.dataEmissao) >= oneWeekAgo;
    });
  }

  if (period === 'today') {
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    return notas.filter(n => {
      if (!n.dataEmissao) return false;
      return new Date(n.dataEmissao) >= startOfToday;
    });
  }

  if (period === 'custom' && customStart && customEnd) {
    const start = new Date(customStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(customEnd);
    end.setHours(23, 59, 59, 999);

    return notas.filter(n => {
      if (!n.dataEmissao) return false;
      const d = new Date(n.dataEmissao);
      return d >= start && d <= end;
    });
  }

  return [...notas];
}

const PERIOD_STORAGE_KEY = 'nfce_current_period_filter';

export function getSavedPeriodFilter() {
  try {
    const raw = localStorage.getItem(PERIOD_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.period) {
        return {
          period: parsed.period,
          customStart: parsed.customStart || null,
          customEnd: parsed.customEnd || null,
          customLabel: parsed.customLabel || ''
        };
      }
    }
  } catch (e) {
    console.warn('Error reading saved period filter:', e);
  }
  return {
    period: 'month',
    customStart: null,
    customEnd: null,
    customLabel: ''
  };
}

export function setSavedPeriodFilter(filter) {
  try {
    const data = {
      period: filter?.period || 'month',
      customStart: filter?.customStart || null,
      customEnd: filter?.customEnd || null,
      customLabel: filter?.customLabel || ''
    };
    localStorage.setItem(PERIOD_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Error saving period filter:', e);
  }
}

export function renderPeriodFilterHTML(activePeriod, customLabel, modalId = 'custom-period-modal') {
  const saved = getSavedPeriodFilter();
  const period = activePeriod !== undefined ? activePeriod : saved.period;
  const label = customLabel !== undefined ? customLabel : (saved.customLabel || '');
  const isCustomActive = period === 'custom' && !!label;
  return `
    <section class="filter-pills" id="period-filters" style="margin-bottom: 1.25rem;">
      <button class="filter-pill ${period === 'custom' ? 'active' : ''}" data-period="custom" id="btn-custom-period" title="${isCustomActive ? `Período ativo: ${label}` : 'Período Personalizado'}" aria-label="Período Personalizado" style="display: inline-flex; align-items: center; justify-content: center; padding: 0.45rem 0.65rem;">
        <span class="material-symbols-outlined" style="font-size: 1.15rem; line-height: 1;">calendar_month</span>
        <span class="custom-period-label ${isCustomActive ? '' : 'hidden'}" style="margin-left: 0.35rem; font-size: 0.75rem; font-weight: 600; white-space: nowrap;">${label}</span>
      </button>
      <button class="filter-pill ${period === '3months' ? 'active' : ''}" data-period="3months">3 Meses</button>
      <button class="filter-pill ${period === 'month' ? 'active' : ''}" data-period="month">Este Mês</button>
      <button class="filter-pill ${period === 'week' ? 'active' : ''}" data-period="week">Esta Semana</button>
    </section>

    <!-- Modal de Período Personalizado -->
    <div id="${modalId}" class="modal-overlay hidden" style="display: none !important;">
      <div class="modal-card custom-period-card">
        <div class="modal-header-row">
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <span class="material-symbols-outlined" style="font-size: 1.5rem; color: var(--color-primary);">calendar_month</span>
            <h3 class="modal-title" style="margin: 0;">Filtrar por Período</h3>
          </div>
          <button class="modal-close-btn" id="btn-close-period-modal" aria-label="Fechar">&times;</button>
        </div>

        <p class="text-muted mb-md" style="font-size: 0.8125rem;">
          Selecione o intervalo de datas desejado ou escolha um atalho rápido:
        </p>

        <!-- Quick range presets inside modal -->
        <div class="custom-range-presets mb-md">
          <button type="button" class="preset-pill-btn" data-range="last365">1 Ano (365 dias)</button>
          <button type="button" class="preset-pill-btn" data-range="last90">Últimos 90 dias</button>
          <button type="button" class="preset-pill-btn" data-range="last60">Últimos 60 dias</button>
          <button type="button" class="preset-pill-btn" data-range="last30">Últimos 30 dias</button>
          <button type="button" class="preset-pill-btn" data-range="lastMonth">Mês Passado</button>
        </div>

        <form id="custom-period-form" class="custom-period-form">
          <div class="date-input-group mb-sm">
            <label for="period-start-date">Data Inicial</label>
            <input type="date" id="period-start-date" class="form-input date-input" required />
          </div>

          <div class="date-input-group mb-md">
            <label for="period-end-date">Data Final</label>
            <input type="date" id="period-end-date" class="form-input date-input" required />
          </div>

          <div id="period-validation-error" class="period-error hidden"></div>

          <div class="flex gap-sm mt-md">
            <button type="submit" class="btn btn-primary" style="flex: 1;">
              Aplicar Filtro
            </button>
            <button type="button" class="btn btn-secondary" id="btn-cancel-custom-period" style="flex: 1;">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}

export function initPeriodFilter(container, options = {}) {
  const saved = getSavedPeriodFilter();
  let activePeriod = options.initialPeriod !== undefined ? options.initialPeriod : saved.period;
  let customStart = options.initialCustomStart !== undefined ? options.initialCustomStart : saved.customStart;
  let customEnd = options.initialCustomEnd !== undefined ? options.initialCustomEnd : saved.customEnd;
  let customLabel = options.initialCustomLabel !== undefined ? options.initialCustomLabel : (saved.customLabel || 'Personalizado');

  const periodFilters = container.querySelector('#period-filters');
  const modalId = options.modalId || 'custom-period-modal';
  const modal = container.querySelector(`#${modalId}`);
  const btnCustomPeriod = container.querySelector('#btn-custom-period');
  const customLabelEl = container.querySelector('.custom-period-label');
  const form = container.querySelector('#custom-period-form');
  const inputStart = container.querySelector('#period-start-date');
  const inputEnd = container.querySelector('#period-end-date');
  const btnClose = container.querySelector('#btn-close-period-modal');
  const btnCancel = container.querySelector('#btn-cancel-custom-period');
  const errorEl = container.querySelector('#period-validation-error');
  const presetButtons = container.querySelectorAll('.preset-pill-btn');

  // Format date helper for input type="date" (YYYY-MM-DD)
  const formatDateToISO = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date helper for label display (DD/MM)
  const formatDateDisplay = (isoStr) => {
    if (!isoStr) return '';
    const parts = isoStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}`;
    }
    return isoStr;
  };

  const openModal = () => {
    const now = new Date();
    if (customEnd) {
      inputEnd.value = customEnd;
    } else if (!inputEnd.value) {
      inputEnd.value = formatDateToISO(now);
    }
    if (customStart) {
      inputStart.value = customStart;
    } else if (!inputStart.value) {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      inputStart.value = formatDateToISO(thirtyDaysAgo);
    }
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    }
    modal.style.setProperty('display', 'flex', 'important');
    modal.classList.remove('hidden');
  };

  const closeModal = () => {
    modal.style.setProperty('display', 'none', 'important');
    modal.classList.add('hidden');
  };

  // Preset pill clicks inside main view
  periodFilters.querySelectorAll('.filter-pill').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const period = btn.getAttribute('data-period');
      if (period === 'custom') {
        openModal();
        return;
      }

      periodFilters.querySelectorAll('.filter-pill').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      activePeriod = period;
      customStart = null;
      customEnd = null;
      customLabel = '';

      if (customLabelEl) {
        customLabelEl.textContent = '';
        customLabelEl.classList.add('hidden');
      }
      btnCustomPeriod?.setAttribute('title', 'Período Personalizado');

      setSavedPeriodFilter({
        period: activePeriod,
        customStart: null,
        customEnd: null,
        customLabel: ''
      });

      if (typeof options.onFilterChange === 'function') {
        options.onFilterChange(activePeriod, null, null);
      }
    });
  });

  // Modal Controls
  btnClose?.addEventListener('click', closeModal);
  btnCancel?.addEventListener('click', closeModal);

  // Close on outside overlay click
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal();
    }
  });

  // Preset buttons inside modal
  presetButtons?.forEach(btn => {
    btn.addEventListener('click', () => {
      const range = btn.getAttribute('data-range');
      const now = new Date();
      let start = new Date();
      let end = new Date();

      if (range === 'last365') {
        start = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      } else if (range === 'last90') {
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      } else if (range === 'last60') {
        start = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
      } else if (range === 'last30') {
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      } else if (range === 'lastMonth') {
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
      }

      inputStart.value = formatDateToISO(start);
      inputEnd.value = formatDateToISO(end);
    });
  });

  // Form submit
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    const startVal = inputStart.value;
    const endVal = inputEnd.value;

    if (!startVal || !endVal) {
      if (errorEl) {
        errorEl.textContent = 'Por favor, preencha a data inicial e final.';
        errorEl.classList.remove('hidden');
      }
      return;
    }

    if (new Date(startVal) > new Date(endVal)) {
      if (errorEl) {
        errorEl.textContent = 'A data inicial não pode ser posterior à data final.';
        errorEl.classList.remove('hidden');
      }
      return;
    }

    customStart = startVal;
    customEnd = endVal;
    activePeriod = 'custom';
    customLabel = `${formatDateDisplay(startVal)} a ${formatDateDisplay(endVal)}`;

    if (customLabelEl) {
      customLabelEl.textContent = customLabel;
      customLabelEl.classList.remove('hidden');
    }

    periodFilters.querySelectorAll('.filter-pill').forEach(b => b.classList.remove('active'));
    btnCustomPeriod?.classList.add('active');
    btnCustomPeriod?.setAttribute('title', `Período ativo: ${customLabel}`);

    setSavedPeriodFilter({
      period: activePeriod,
      customStart,
      customEnd,
      customLabel
    });

    closeModal();

    if (typeof options.onFilterChange === 'function') {
      options.onFilterChange(activePeriod, customStart, customEnd);
    }
  });

  return {
    getActivePeriod: () => activePeriod,
    getCustomStart: () => customStart,
    getCustomEnd: () => customEnd,
    filter: (notas) => filterNotasByPeriod(notas, activePeriod, customStart, customEnd)
  };
}
